import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, GraduationCap, Sparkles, Megaphone } from "lucide-react";

/**
 * Sticky Pre-Header-Ankündigungsleiste — rotiert zwischen zwei Slots:
 *   Slot A: Externenprüfung Pflegefachassistenz (MG-Partnerschule)
 *   Slot B: Pflege-KI Kompakt — Digitale Patientenmappe
 *
 * Auto-Wechsel alle 6 Sekunden mit sanftem Fade. Dismiss speichert per
 * sessionStorage — pro Browser-Session eine bewusste Entscheidung.
 */
const SLOTS = [
  {
    id: "ausbildung",
    active: false,
    to: "/#kontakt",
    Icon: GraduationCap,
    strong: "Ausbildungsplätze zum 01.11.",
    text: "· 3-jährige Pflegeausbildung · Partnerschule Mönchengladbach",
    shortStrong: "Ausbildung ab 01.11.",
    shortText: "· Plätze frei",
    bg: "bg-espresso-deep",
    bgHover: "hover:bg-espresso-card",
    bgDismiss: "hover:bg-espresso-card",
    accent: "text-saffron",
    onClick: () => {
      try {
        sessionStorage.setItem("dh-lead-prefill", JSON.stringify({
          course_interest: "Ausbildung Pflegefachkraft (3 Jahre) – Partnerschule Mönchengladbach",
          message: "Ich interessiere mich für die 3-jährige Pflegeausbildung zum 01.11. bei der Partnerschule in Mönchengladbach.",
        }));
      } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent("dh-lead-prefill"));
      document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
  {
    id: "services",
    to: "/services",
    Icon: Megaphone,
    strong: "Pflegedienst? Neue Homepage ab 1.990 € Festpreis",
    text: "· Google Ads & Werbung an jedes Budget anpassbar",
    shortStrong: "Homepage ab 1.990 €",
    shortText: "· für Pflegedienste",
    bg: "bg-espresso-deep",
    bgHover: "hover:bg-espresso-card",
    bgDismiss: "hover:bg-espresso-card",
  },
  {
    id: "pfa",
    active: false,
    to: "/externenpruefung-pflegefachassistenz",
    Icon: GraduationCap,
    strong: "Externenprüfung Pflegefachassistenz",
    text: "· Prüfungsplätze über unsere Partnerschulen in NRW · Plätze frei",
    shortStrong: "Externenprüfung PFA",
    shortText: "· Plätze frei",
    bg: "bg-espresso",
    bgHover: "hover:bg-espresso-card",
    bgDismiss: "hover:bg-espresso-card",
  },
  {
    id: "pki",
    active: false,
    to: "/campus/pro",
    Icon: Sparkles,
    strong: "Pflege-KI Kompakt · Digitale Patientenmappe",
    text: "· 11 Sektionen · 26 QPR-Kriterien · Team-Chat · ab 199 €",
    shortStrong: "Pflege-KI Kompakt",
    shortText: "· ab 199 €",
    bg: "bg-espresso-deep",
    bgHover: "hover:bg-espresso-card",
    bgDismiss: "hover:bg-espresso-card",
  },
];

const ACTIVE_SLOTS = SLOTS.filter((s) => s.active !== false);

export default function AnnouncementBar() {
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem("dh-announce-dismissed-v4") === "1"; } catch { return false; }
  });
  const [slot, setSlot] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    if (hidden || ACTIVE_SLOTS.length < 2) return;
    const rotate = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setSlot((s) => (s + 1) % ACTIVE_SLOTS.length);
        setFadeIn(true);
      }, 260);
    }, 6000);
    return () => clearInterval(rotate);
  }, [hidden]);

  if (hidden || ACTIVE_SLOTS.length === 0) return null;
  const s = ACTIVE_SLOTS[slot % ACTIVE_SLOTS.length];

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem("dh-announce-dismissed-v4", "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div
      data-testid="announcement-bar"
      data-slot={s.id}
      className={`w-full ${s.bg} text-cream-100 sticky top-0 z-[70] transition-colors duration-500 relative`}
    >
      <Link
        to={s.to}
        onClick={s.onClick}
        data-testid={`announcement-bar-link-${s.id}`}
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-10 py-2 flex items-center justify-center gap-3 text-[12.5px] sm:text-[13px] font-medium ${s.bgHover} transition-all duration-300 text-center pr-10 ${fadeIn ? "opacity-100" : "opacity-0"}`}
      >
        <s.Icon className={`h-4 w-4 shrink-0 ${s.accent || "text-brand-rose"}`} strokeWidth={1.9} />
        <span className="hidden sm:inline">
          <strong className={`font-semibold ${s.accent || "text-brand-rose"}`}>{s.strong}</strong>
          {" "}{s.text}
        </span>
        <span className="sm:hidden">
          <strong className={`font-semibold ${s.accent || "text-brand-rose"}`}>{s.shortStrong}</strong> {s.shortText}
        </span>
        <span className="inline-flex items-center gap-1 opacity-95">
          Mehr <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
      </Link>

      {/* Slot-Indicator (dezent) — nur bei Rotation */}
      {ACTIVE_SLOTS.length > 1 && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1">
          {ACTIVE_SLOTS.map((_, i) => (
            <span
              key={i}
              className={`h-1 w-1 rounded-full transition-all ${i === slot ? "bg-white w-3" : "bg-white/40"}`}
              aria-hidden="true"
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={dismiss}
        aria-label="Ankündigung ausblenden"
        data-testid="announcement-bar-dismiss"
        className={`absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-full text-white/85 ${s.bgDismiss} hover:text-white shrink-0`}
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
